import { atom, WritableAtom } from 'jotai';
import { isAxiosError, isCancel } from 'axios';

import { hasInternetConnectionAtom } from '_atoms/navigation';
import { resetAppAtom } from '_atoms/resetAction';
import { isUnauthorized } from '_helpers/queryInterceptorHelpers';

type ErrorHandlerProps = {
  /** The error object to handle */
  error: unknown;

  /** Atom in which to store the error */
  errorAtom: WritableAtom<Error | null, [Error | null], void>;

  /** Prefix for console logging */
  consolePreamble: string;
}


/**
 * Centralized error handler atom.
 *
 * Behavior:
 * - Silently ignores canceled requests.
 * - Marks the app as offline if the error is network-level (no HTTP response).
 * - Triggers logout if the error is Unauthorized (401) or Forbidden (403).
 * - Otherwise, records the error in the provided atom and logs it to the console.
 */
export const handleErrorAtom = atom(
  null,
  (_get, set, {error, errorAtom, consolePreamble}: ErrorHandlerProps) => {
    // Cancelled requests -> ignore silently
    if (isCancel(error)) return;

    // Network-level failure: no HTTP response (offline, DNS, CORS, timeout before response)
    if (isAxiosError(error) && !error.response) {
      set(hasInternetConnectionAtom, false);
      return;
    }

    // Unauthorized/Forbidden -> force logout
    if (isUnauthorized(error)) {
      set(resetAppAtom);
      return;
    }

    // Fallback: record the error for display/logging
    const safeError: Error = error instanceof Error ? error : new Error(String(error));
    set(errorAtom, safeError);
    console.error(consolePreamble, error);
  }
);